import React, { useEffect, useState } from "react";
import { RadioButton } from "../../../../../components/core/RadioButton/RadioButton";
import {
  StyledTableSoftSkills,
  StyledTableTd,
  TableHeader,
  TableRow,
  StyledTitleTd,
  StyledTableContainer,
} from "./SoftSkills.style";
import {
  ASSIGN_LABEL,
  EMPTY_SOFT_SKILLS_MESSAGE,
} from "../../../../../constants/Dictionary";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import { Tooltip } from "../../../../../components/core/Tooltip/Tooltip";
import { FontWeightEnum, IconSizeEnum } from "../../../../../types/AppTypes";
import { theme } from "../../../../../constants/StyledComponents";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { ReactComponent as InfoIcon } from "../../../../../icons/info.svg";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { Controller, useForm } from "react-hook-form";
import { UpdateCollaboratorCmSheetSoftSkillsRequest } from "../../../../../types/api/collaborators.type";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { ReactComponent as UserCheckIcon } from "../../../../../icons/usersCheck.svg";
import softSkillsImage from "../../../../../images/softSkills.png";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import { CmSheetSectionProps } from "../SchedaCM";
import {
  useCmSheetSoftSkills,
  useUpdateCmSheetSoftSkills,
} from "../../../../../hooks/cmSheet/softSkills.hook";
import {
  StyledScrollableTableWrapper,
  StyledTable,
} from "../../../../../components/core/Table/Table.styled";
import { TableThemeEnum } from "../../../../../components/core/Table/Table";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import {
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";

type Item = {
  dimensionId: number;
  levelId: number;
};

type FormData = {
  items: Item[];
};

export const SoftSkills = ({
  footer,
  cmSheetInfo,
  cmSheetStateMachineInfo,
  showFooter,
  isMyProfile,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { config } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);

  const [hasData, setHasData] = useState<boolean | null>(null);

  const selectedYearConfig = config.find(
    (item) => item.year === cmSheetInfo.configYear,
  );

  const { data, isFetching } = useCmSheetSoftSkills(cmSheetInfo.id);

  const { updateSoftSkills, isUpdating: updating } = useUpdateCmSheetSoftSkills(
    cmSheetInfo.id,
  );

  const {
    handleSubmit,
    reset,
    control,
    formState: { dirtyFields },
  } = useForm<FormData>();

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (isFetching || !data) {
      return;
    }

    if (selectedYearConfig) {
      reset({
        items: data
          ? selectedYearConfig.config.softSkillsDimensions.map((dimension) => ({
              dimensionId: dimension.id,
              levelId: data.find((level) => dimension.id === level.dimensionId)
                ?.levelId,
            }))
          : [],
      });
    }

    if (hasData === null) {
      const hasAnyAssignedSkills = data.some((item) => item.levelId);
      setHasData(hasAnyAssignedSkills);
    }
  }, [data, selectedYearConfig, hasData, isFetching, reset]);

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData && !edit;
  const showPage = hasData === true || edit;

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="softSkillsForm"
        disabledEditButton={!dirtyFields.items || updating || isFetching}
        edit={edit}
        showEdit={canEdit() && (!!hasData || edit)}
        setEdit={setEdit}
        showFooter={showFooter}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
      />,
    );
  }, [
    dirtyFields.items,
    edit,
    hasData,
    isFetching,
    updating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  if (!config) {
    return null;
  }

  const onSubmit = async (data: FormData) => {
    try {
      const body: UpdateCollaboratorCmSheetSoftSkillsRequest = [];
      data.items.forEach((item) => {
        if (item.dimensionId && item.levelId) {
          body.push({
            dimensionId: Number(item.dimensionId),
            levelId: Number(item.levelId),
          });
        }
      });

      await updateSoftSkills(body);
    } finally {
      setHasData(null);
      setEdit(false);
    }
  };

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={canEdit() ? EMPTY_SOFT_SKILLS_MESSAGE : ""}
            imageSrc={softSkillsImage}
          >
            {canEdit() && (
              <Button
                label={ASSIGN_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<UserCheckIcon />}
                onClick={() => (setEdit ? setEdit(true) : {})}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={updating}>
          <StyledHideOrShowContainer $show={edit}>
            <StyledTableContainer>
              <form onSubmit={handleSubmit(onSubmit)} id="softSkillsForm">
                <StyledTableSoftSkills>
                  <colgroup>
                    <col style={{ width: "50%", minWidth: "50%" }} />
                  </colgroup>
                  <thead>
                    <TableRow>
                      <TableHeader />
                      {selectedYearConfig?.config.softSkillsLevels.map(
                        (item) => {
                          return (
                            <TableHeader key={item.id}>
                              {edit && (
                                <div>
                                  <BodyTyp
                                    fontWeight={FontWeightEnum.SEMIBOLD}
                                    variant="2"
                                  >
                                    {item.name}
                                  </BodyTyp>
                                  <Tooltip message={item.description}>
                                    <IconManager size={IconSizeEnum.SMALL}>
                                      <InfoIcon />
                                    </IconManager>
                                  </Tooltip>
                                </div>
                              )}
                            </TableHeader>
                          );
                        },
                      )}
                    </TableRow>
                  </thead>
                </StyledTableSoftSkills>
                <StyledScrollableTableWrapper
                  $height="calc(100dvh - 499px)"
                  $theme={TableThemeEnum.LIGHT}
                >
                  <StyledTable>
                    <colgroup>
                      <col style={{ width: "50%", minWidth: "50%" }} />
                    </colgroup>
                    <tbody>
                      {selectedYearConfig?.config.softSkillsDimensions?.map(
                        (dimensionItem, rowIndex) => {
                          return (
                            <TableRow key={`${dimensionItem.id}-${rowIndex}`}>
                              <StyledTitleTd>
                                <BodyTyp
                                  variant="1"
                                  fontWeight={FontWeightEnum.SEMIBOLD}
                                >
                                  {dimensionItem.name}
                                </BodyTyp>
                                <BodyTyp
                                  variant="1"
                                  fontWeight={FontWeightEnum.REGULAR}
                                >
                                  {dimensionItem.description}
                                </BodyTyp>
                              </StyledTitleTd>
                              {selectedYearConfig?.config.softSkillsLevels.map(
                                (levelItem, colIndex) => {
                                  return (
                                    <StyledTableTd
                                      key={`${levelItem.id}-${colIndex}`}
                                      align="center"
                                    >
                                      <Controller
                                        name={`items.${rowIndex}.dimensionId`}
                                        control={control}
                                        render={({ field }) => (
                                          <input type="hidden" {...field} />
                                        )}
                                      />
                                      <Controller
                                        name={`items.${rowIndex}.levelId`}
                                        control={control}
                                        render={({
                                          field: { onChange, value },
                                        }) => (
                                          <RadioButton
                                            onChange={() =>
                                              onChange(levelItem.id)
                                            }
                                            value={levelItem.id === value}
                                          />
                                        )}
                                      />
                                    </StyledTableTd>
                                  );
                                },
                              )}
                            </TableRow>
                          );
                        },
                      )}
                    </tbody>
                  </StyledTable>
                </StyledScrollableTableWrapper>
              </form>
            </StyledTableContainer>
          </StyledHideOrShowContainer>
          <StyledHideOrShowContainer $show={!edit}>
            <StyledTableContainer>
              <StyledTableSoftSkills>
                <tbody>
                  {selectedYearConfig?.config.softSkillsDimensions?.map(
                    (dimensionItem) => {
                      return (
                        <TableRow key={dimensionItem.id}>
                          <StyledTitleTd>
                            <BodyTyp
                              variant="1"
                              fontWeight={FontWeightEnum.SEMIBOLD}
                            >
                              {dimensionItem.name}
                            </BodyTyp>
                            <BodyTyp
                              variant="1"
                              fontWeight={FontWeightEnum.REGULAR}
                            >
                              {dimensionItem.description}
                            </BodyTyp>
                          </StyledTitleTd>
                          <StyledTableTd>
                            <div className={"levelItem-right"}>
                              <BodyTyp
                                variant="1"
                                fontWeight={FontWeightEnum.SEMIBOLD}
                                color={theme.colors.purple}
                              >
                                {
                                  selectedYearConfig.config.softSkillsLevels.find(
                                    (item) =>
                                      item.id ===
                                      data?.find(
                                        (item) =>
                                          item.dimensionId === dimensionItem.id,
                                      )?.levelId,
                                  )?.name
                                }
                              </BodyTyp>
                            </div>
                          </StyledTableTd>
                        </TableRow>
                      );
                    },
                  )}
                </tbody>
              </StyledTableSoftSkills>
            </StyledTableContainer>
          </StyledHideOrShowContainer>
        </Loader>
      </StyledHideOrShowContainer>
    </>
  );
};
